import { AfterViewInit, Component, Input, OnInit, inject } from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {finalize} from 'rxjs/operators';

import {HeaderSort, HtmlEntities} from '@shared';
import {
    BreakpointObserverService,
    BreakpointValue,
    EmployeeService,
    FeasibilityService,
    Patient,
    PatientTaskService,
    PatientTaskView,
    ProblemInstanceService
} from 'planner-shared';
import {TaskColorService} from "../../shared/services/task-color/task-color.service";
import {FeatureFlags, FeatureFlagService} from "@core";
import {Observable, Subject} from "rxjs";
import {
    DialogInteractableService
} from "../../../../_shared/services/dialog-interactable/dialog-interactable.service";
import {ColorPickerComponent} from "../../../../_shared/color-picker/color-picker.component";
import {Placement} from "../../../../_shared/models/placement.model";

@UntilDestroy()
@Component({
    selector: 'app-patient-tasks',
    templateUrl: './patient-tasks.component.html',
    styleUrls: ['./patient-tasks.component.scss'],
    standalone: false
})
export class PatientTasksComponent implements OnInit, AfterViewInit {
    private readonly employeeService = inject(EmployeeService);
    private readonly patientTaskService = inject(PatientTaskService);
    private readonly feasibilityService = inject(FeasibilityService);
    private readonly breakPointObserver = inject(BreakpointObserverService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly taskColorService = inject(TaskColorService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly dialogInteractableService = inject(DialogInteractableService);

    @Input()
    public set patient(patient: Patient) {
        this._patient = patient;
        this.editing = false;
        this.initTasks();
        if (this.colorPickSubject) {
            this.dialogInteractableService.close();
        }
    }

    public get patient(): Patient {
        return this._patient;
    }

    public tasks: PatientTaskView[];
    public loading: boolean;
    public editing: boolean;

    private readonly InitSortBy = 'fromTime';
    private readonly InitSortBySecondary = 'toTime';

    private _patient: Patient;
    private colorPickSubject: Subject<string>;

    public restrictionId: string;
    public showRestrictionContextMenu: boolean;
    public showRestrictionContextMenuTopPosition: number;
    public showRestrictionContextMenuLeftPosition: number;
    public showRoster: boolean;
    public breakPointValue: BreakpointValue;
    public coloringTaskFeatureEnabled: Observable<boolean>;

    protected readonly BreakpointValue = BreakpointValue;

    ngOnInit(): void {
        this.showRestrictionContextMenu = false;
        this.patientTaskService
            .getPatientTaskEditStateChange()
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.editing = false));
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe(untilDestroyed(this))
            .subscribe(result => this.showRoster = result.valueOf());
        this.breakPointObserver.getCurrentBreakpoint()
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                this.breakPointValue = value;
            });
        this.coloringTaskFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.ColoringTasks);
    }

    ngAfterViewInit(): void {
        document.addEventListener('click', () => {
            this.showRestrictionContextMenu = false;
        });
    }

    public edit(task: PatientTaskView, event: Event): void {
        if (
            !this.editing &&
            !this.patient.taskUpdating &&
            !this.patient.addressUpdating &&
            !this.patient.feasibilityReloading
        ) {
            event.stopPropagation();
            this.editing = true;
            task.editing = true;
        }
    }

    public reloadTasksFeasibilityInfo(patientId: string): void {
        if (patientId === this.patient.patientId) {
            this.patientTaskService.getPatientTasksFeasibility().subscribe(feasibility => {
                this.tasks.forEach(task => {
                    task.updating = false;
                    task.feasibilityInfo = this.feasibilityService.getTaskFeasibilityInfo(
                        task.originalTaskId,
                        feasibility
                    );
                });
            });
        }
    }

    public openModalWindow(task: PatientTaskView, e) {
        e.preventDefault();
        this.showRestrictionContextMenu = true;
        this.restrictionId = task.originalTaskId;
    }

    public getMousePosition(e) {
        this.showRestrictionContextMenuTopPosition = e.layerY;
        this.showRestrictionContextMenuLeftPosition = e.layerX;
    }

    public openEmployeesRestrictionModal(): void {
        this.showRestrictionContextMenu = false;
        this.employeeService.openEmployeesRestrictionModal(this.restrictionId);
    }

    public initTasks(): void {
        const sort = <HeaderSort>{
            sortBy: this.InitSortBy,
            sortAscending: true,
            secondarySortBy: this.InitSortBySecondary,
            secondarySortAscending: true
        };

        this.loading = true;
        this.tasks = new Array();
        this.patientTaskService
            .getPatientTasks(this.patient.patientId, sort)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(tasks => {
                this.tasks = tasks.map(task => {
                    task.taskDescription = HtmlEntities.decode(task.taskDescription);
                    return task;
                });
                this.taskColorService.getTaskColors().forEach(taskColor => {
                    const coloredTask = this.tasks.find(task => task.taskId === taskColor.taskId);
                    if (coloredTask) {
                        coloredTask.color = taskColor.color;
                    }
                });
            });
    }

    public getHiddenInitials(task: PatientTaskView): string {
        return task.mainResponsibleEmployees
            .slice(1, task.mainResponsibleEmployees.length)
            .map(i => i.initials)
            .join();
    }

    public taskColorChanged(task: PatientTaskView, color: string): void {
        task.color = color;
        this.taskColorService.addColorToTask(color, task.taskId);
    }

    public openColorPopover(task: PatientTaskView, event: Event) {
        const targetElement = event.target as HTMLElement;
        this.colorPickSubject
            = this.dialogInteractableService.open(targetElement, ColorPickerComponent, Placement.Top, task.color);

        this.colorPickSubject
            .subscribe(color => {
                this.taskColorChanged(task, color);
            });
    }
}
